import type { MissionTrigger, TableRules } from './tables/definition';

export interface RuleInputEvent {
  type: string;
  partId?: string;
  destroyed?: boolean;
  dropped?: boolean;
  remainingHits?: number;
}

export interface MissionSnapshot {
  id: string;
  name: string;
  description?: string;
  progress: number;
  target: number;
}

export interface RuleSnapshot {
  mission: MissionSnapshot | null;
  completedMissions: number;
  totalMissions: number;
  combo: number;
  allComplete: boolean;
}

export interface RuleOutcome {
  bonus: number;
  jackpot: boolean;
}

function triggerFor(event: RuleInputEvent): MissionTrigger | null {
  if (
    event.type === 'bumper' ||
    event.type === 'slingshot' ||
    event.type === 'target' ||
    event.type === 'rollover' ||
    event.type === 'ramp' ||
    event.type === 'kicker' ||
    event.type === 'prop-destroy'
  ) return event.type;
  return null;
}

export class RuleTracker {
  private rules: TableRules = {};
  private missionIndex = 0;
  private missionProgress = 0;
  private missionPartsHit = new Set<string>();
  private missionHitCounts = new Map<string, number>();
  private targetHitCounts = new Map<string, number>();
  private retiredTargets = new Set<string>();
  private destroyedProps = new Set<string>();
  private combo = 0;
  private lastHitAt = -Infinity;
  private lastPartId: string | null = null;
  private completionAwarded = false;

  reset(rules: TableRules | undefined): void {
    this.rules = rules ?? {};
    this.missionIndex = 0;
    this.missionProgress = 0;
    this.missionPartsHit.clear();
    this.missionHitCounts.clear();
    this.targetHitCounts.clear();
    this.retiredTargets.clear();
    this.destroyedProps.clear();
    this.combo = 0;
    this.lastHitAt = -Infinity;
    this.lastPartId = null;
    this.completionAwarded = false;
  }

  private comboWindowSeconds(): number {
    return Math.max(0, this.rules.comboWindowMs ?? 2200) / 1000;
  }

  apply(event: RuleInputEvent, simulationTimeSeconds: number): RuleOutcome {
    const trigger = triggerFor(event);
    if (!trigger) return { bonus: 0, jackpot: false };

    let bonus = 0;
    let jackpot = false;
    const partId = event.partId ?? null;
    const comboWindow = this.comboWindowSeconds();
    const comboStep = Math.max(0, this.rules.comboStepBonus ?? 0);
    // Physical targets stay down for the game. Preserve their completed state
    // even if a player clears one before its mission becomes current.
    if (partId && trigger === 'target' && !this.retiredTargets.has(partId)) {
      if (event.remainingHits !== undefined || event.dropped || event.destroyed) {
        this.targetHitCounts.set(partId,(this.targetHitCounts.get(partId) ?? 0)+1);
      }
      if (event.dropped || event.destroyed) this.retiredTargets.add(partId);
    }
    if (partId && trigger === 'prop-destroy') this.destroyedProps.add(partId);

    // Destruction is an extra rules moment emitted for the same physical hit,
    // so it must not reset/increment the shot combo a second time.
    if (trigger !== 'prop-destroy') {
      if (
        partId &&
        this.lastPartId &&
        partId !== this.lastPartId &&
        simulationTimeSeconds - this.lastHitAt <= comboWindow
      ) {
        this.combo += 1;
        if (this.combo >= 2) bonus += comboStep * (this.combo - 1);
      } else {
        this.combo = 1;
      }
      this.lastHitAt = simulationTimeSeconds;
      this.lastPartId = partId;
    }

    const missions = this.rules.missions ?? [];
    const mission = missions[this.missionIndex];
    if (mission) {
      const target = Math.max(1, mission.count);
      const partMatches = !mission.partIds?.length || (partId ? mission.partIds.includes(partId) : false);
      if (mission.trigger === trigger && partMatches) {
        // When a mission explicitly enumerates exactly the number of mechanisms
        // it asks the player to hit, treat them as a set: each one must be hit
        // once. Missions that ask for more shots than named mechanisms (for
        // example, one ramp three times) intentionally continue to allow repeats.
        const distinctPartsRequired = Boolean(mission.partIds?.length === target);
        const alreadyCounted = Boolean(distinctPartsRequired && partId && this.missionPartsHit.has(partId));
        if (!alreadyCounted) {
          if (distinctPartsRequired && partId) this.missionPartsHit.add(partId);
          if (partId) this.missionHitCounts.set(partId,(this.missionHitCounts.get(partId) ?? 0)+1);
          this.missionProgress += 1;
        }
      }
    }

    // Newly reached stages can refer to a target that no longer exists. Credit
    // only irreversible target/destruction progress; ramps, pops and catches
    // still need fresh shots during their own stage.
    while (this.missionIndex < missions.length) {
      const current=missions[this.missionIndex]!;
      const target=Math.max(1,current.count);
      const distinct=current.partIds?.length === target;
      const completed=current.trigger === 'target' ? this.retiredTargets
        : current.trigger === 'prop-destroy' ? this.destroyedProps : new Set<string>();
      for (const id of completed) {
        if (current.partIds?.length && !current.partIds.includes(id)) continue;
        const available=distinct || current.trigger === 'prop-destroy' ? 1 : this.targetHitCounts.get(id) ?? 1;
        const credited=this.missionHitCounts.get(id) ?? 0;
        if (available>credited) {
          this.missionProgress += available-credited;
          this.missionHitCounts.set(id,available);
          this.missionPartsHit.add(id);
        }
      }
      if (this.missionProgress<target) break;
      bonus+=Math.max(0,current.bonus);
      this.missionIndex++;
      this.missionProgress=0;
      this.missionPartsHit.clear();
      this.missionHitCounts.clear();
      if (this.missionIndex>=missions.length && !this.completionAwarded) {
        bonus+=Math.max(0,this.rules.completionBonus ?? 0);
        this.completionAwarded=true;
        jackpot=true;
      }
    }

    return { bonus, jackpot };
  }

  snapshot(simulationTimeSeconds = this.lastHitAt): RuleSnapshot {
    const missions = this.rules.missions ?? [];
    const mission = missions[this.missionIndex];
    const comboActive =
      Number.isFinite(this.lastHitAt) &&
      Number.isFinite(simulationTimeSeconds) &&
      simulationTimeSeconds - this.lastHitAt <= this.comboWindowSeconds();
    return {
      mission: mission
        ? {
            id: mission.id,
            name: mission.name,
            description: mission.description,
            progress: this.missionProgress,
            target: Math.max(1, mission.count),
          }
        : null,
      completedMissions: Math.min(this.missionIndex, missions.length),
      totalMissions: missions.length,
      combo: comboActive ? this.combo : 0,
      allComplete: missions.length > 0 && this.missionIndex >= missions.length,
    };
  }
}
